import { createElement, type ReactElement } from "react";
import type { ZudokuPlugin } from "zudoku";
import type { PageMetaMap } from "./page-meta.js";

const SITE_URL = "https://docs.spectate.net";
const ORG_URL = "https://spectate.net";
const ORG_ID = `${ORG_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
/** The marketing site's share image, so a shared docs link previews with the brand. */
const SOCIAL_IMAGE = `${ORG_URL}/og/index`;

/** Slug parts written in capitals, so "api-tokens" reads "API tokens". */
const ACRONYMS = new Set(["api", "dns", "faq", "http", "ip", "json", "mcp", "tcp", "tls"]);

const segmentLabel = (segment: string) => {
  const words = segment.split("-");

  return words
    .map((word, index) => {
      if (ACRONYMS.has(word)) return word.toUpperCase();
      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    })
    .join(" ");
};

const ldScript = (data: Record<string, unknown>, key: string) =>
  createElement("script", { key, type: "application/ld+json" }, JSON.stringify(data).replace(/</g, "\\u003c"));

const meta = (attribute: "name" | "property", key: string, content: string) =>
  createElement("meta", { key: `${attribute}-${key}`, [attribute]: key, content });

const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Spectate",
  url: ORG_URL,
  logo: `${SITE_URL}/favicon.svg`,
  email: "help@spectate.net",
};

const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "Spectate Docs",
  url: SITE_URL,
  publisher: { "@id": ORG_ID },
};

/**
 * Structured data and social metadata for every page: Organization and
 * WebSite, a BreadcrumbList, a TechArticle for authored pages, and Open Graph
 * tags (Zudoku emits none, so shared links would preview as bare URLs).
 * Helps search engines and AI crawlers place each page.
 */
export const jsonLdPlugin = (pages: PageMetaMap = {}): ZudokuPlugin => ({
  getHead({ location }) {
    const path = location.pathname.replace(/\/$/, "") || "/";
    const segments = path === "/" ? [] : path.split("/").filter(Boolean);
    const url = `${SITE_URL}${path === "/" ? "" : path}`;
    const page = pages[path];

    const trail: { name: string; item: string }[] = [{ name: "Docs", item: `${SITE_URL}/introduction` }];
    let running = "";

    segments.forEach((segment, index) => {
      running += `/${segment}`;
      const known = pages[running] ?? pages[`${running}/introduction`];
      const isLeaf = index === segments.length - 1;

      if (isLeaf || known) {
        trail.push({
          name: (isLeaf ? page?.title : known?.title) ?? segmentLabel(segment),
          item: `${SITE_URL}${pages[running] || isLeaf ? running : `${running}/introduction`}`,
        });
      }
    });

    const items = trail.filter((entry, index) => entry.item !== trail[index + 1]?.item);
    const title = page?.title ? `${page.title} | Spectate Docs` : undefined;

    const head: (ReactElement | null)[] = [
      ldScript(ORGANIZATION, "ld-org"),
      ldScript(WEBSITE, "ld-website"),
      items.length > 1
        ? ldScript(
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: items.map((entry, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: entry.name,
                item: entry.item,
              })),
            },
            "ld-breadcrumb",
          )
        : null,
      page
        ? ldScript(
            {
              "@context": "https://schema.org",
              "@type": "TechArticle",
              headline: page.title ?? segmentLabel(segments.at(-1) ?? ""),
              ...(page.description && { description: page.description }),
              url,
              inLanguage: "en",
              author: { "@id": ORG_ID },
              publisher: { "@id": ORG_ID },
              isPartOf: { "@id": WEBSITE_ID },
            },
            "ld-article",
          )
        : null,
      meta("property", "og:site_name", "Spectate Docs"),
      meta("property", "og:url", url),
      meta("property", "og:type", page ? "article" : "website"),
      meta("property", "og:image", SOCIAL_IMAGE),
      meta("name", "twitter:card", "summary_large_image"),
      meta("name", "twitter:image", SOCIAL_IMAGE),
      ...(title ? [meta("property", "og:title", title), meta("name", "twitter:title", title)] : []),
      ...(page?.description
        ? [meta("property", "og:description", page.description), meta("name", "twitter:description", page.description)]
        : []),
      // The Markdown twin of the page, for agents (`publishMarkdown`)
      page ? createElement("link", { key: "alternate-md", rel: "alternate", type: "text/markdown", href: `${path}.md` }) : null,
    ];

    return head.filter((element): element is ReactElement => element !== null);
  },
});
